/**
 * REGISTA AI: Waitlist Signup Component
 * 
 * Early access signup form with Workers AI integration
 */

import { useState } from 'react';
import { ArrowRight, CheckCircle, Loader, Mail, User, Building } from 'lucide-react';
import { analytics } from '../lib/analytics';

interface WaitlistSignupProps {
  className?: string;
  variant?: 'inline' | 'modal' | 'section';
}

export default function WaitlistSignup({ className = '', variant = 'inline' }: WaitlistSignupProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [useCase, setUseCase] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [position, setPosition] = useState<string | number>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Track form submission
    analytics.waitlistFormSubmit('landing_page', !!(name || company || useCase));

    try {
      const response = await fetch('/api/waitlist/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          name: name || undefined,
          company: company || undefined,
          use_case: useCase || undefined,
          referral_source: 'landing_page'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setPosition(data.position);
        
        // Track successful signup
        analytics.waitlistSuccess(data.position, 'landing_page', {
          hasName: !!name,
          hasCompany: !!company,
          useCase: useCase || 'not_specified'
        });
        
        // Clear form
        setEmail('');
        setName('');
        setCompany('');
        setUseCase('');
      } else {
        const errorMsg = data.error || 'Failed to join waitlist';
        setError(errorMsg);
        analytics.waitlistError(errorMsg, 'landing_page');
      }
    } catch (err) {
      const errorMsg = 'Network error. Please try again.';
      setError(errorMsg);
      analytics.waitlistError(errorMsg, 'landing_page');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className={`text-center ${className}`}>
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-4">
          <CheckCircle className="w-8 h-8" />
        </div>
        <h3 className="text-xl font-semibold text-dark-900 mb-2">
          You're in! 🎉
        </h3>
        <p className="text-dark-600 mb-4">
          {typeof position === 'number' ? 
            `You're #${position} on the waitlist for early access to RegistaAI.` :
            'Welcome to the RegistaAI waitlist!'
          }
        </p>
        <div className="text-sm text-dark-500">
          We'll email you when it's your turn to access our 88% cost-optimized AI video platform.
        </div>
      </div>
    );
  }

  const isInline = variant === 'inline';
  const isSection = variant === 'section';

  return (
    <div className={className}>
      {isSection && (
        <div className="text-center mb-8">
          <h2 className="text-3xl font-display font-bold text-dark-900 mb-4">
            Join the Waitlist
          </h2>
          <p className="text-xl text-dark-600 max-w-2xl mx-auto">
            Be among the first to access our edge-native AI video platform with 88% cost savings
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className={isInline ? 'flex gap-2' : 'space-y-4 max-w-md mx-auto'}>
        {!isInline && (
          <>
            <div>
              <label className="block text-sm font-medium text-dark-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                Name (optional)
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-dark-900 placeholder-dark-500"
                placeholder="Your name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-700 mb-2">
                <Building className="w-4 h-4 inline mr-1" />
                Company (optional)
              </label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="w-full px-4 py-3 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-dark-900 placeholder-dark-500"
                placeholder="Your company"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-700 mb-2">
                Use Case (optional)
              </label>
              <select
                value={useCase}
                onChange={(e) => setUseCase(e.target.value)}
                className="w-full px-4 py-3 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-dark-900"
              >
                <option value="">Select your primary use case</option>
                <option value="marketing">Marketing Videos</option>
                <option value="social_media">Social Media Content</option>
                <option value="product_demos">Product Demos</option>
                <option value="training">Training & Education</option>
                <option value="entertainment">Entertainment</option>
                <option value="other">Other</option>
              </select>
            </div>
          </>
        )}

        <div className={isInline ? 'flex-1' : ''}>
          <label className={`block text-sm font-medium text-dark-700 ${isInline ? 'sr-only' : 'mb-2'}`}>
            <Mail className="w-4 h-4 inline mr-1" />
            Email Address
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`px-4 py-3 border border-dark-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-dark-900 placeholder-dark-500 ${
              isInline ? 'w-full' : 'w-full'
            }`}
            placeholder="Enter your email"
          />
        </div>

        <button
          type="submit"
          disabled={isLoading || !email}
          className={`btn btn-primary ${isInline ? 'px-6' : 'w-full btn-lg'} ${
            isLoading ? 'opacity-75' : ''
          }`}
        >
          {isLoading ? (
            <>
              <Loader className="w-5 h-5 mr-2 animate-spin" />
              Joining...
            </>
          ) : (
            <>
              Join Waitlist
              <ArrowRight className="w-5 h-5 ml-2" />
            </>
          )}
        </button>

        {error && (
          <div className={`text-red-600 text-sm ${isInline ? 'absolute mt-1' : 'mt-2'}`}>
            {error}
          </div>
        )}
      </form>

      {!isInline && (
        <div className="mt-6 text-center text-sm text-dark-800">
          <p className="mb-1">Join 1000+ creators waiting for early access</p>
          <p className="text-dark-700">✨ 88% cost savings • 🚀 Edge processing • 🔒 EU jurisdiction</p>
        </div>
      )}
    </div>
  );
}